import { useEffect, useState } from "react";
import api, { errMsg } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const WO_TYPES = ["PM01", "PM02", "PM03", "PM04"];
const PRIORITIES = ["low", "med", "high", "critical"];
export const WO_OPEN_STATUSES = ["created", "released", "in_progress", "tech_completed"];

/** Full PUT body for /mnt/work-orders/{id}: current WO merged with a patch. */
export const woPutBody = (wo, patch = {}) => ({
  type: wo.type, equipment_id: wo.equipment_id, priority: wo.priority,
  description: wo.description, notification_id: wo.notification_id || "",
  assigned_to: wo.assigned_to || "", technician_name: wo.technician_name || "",
  planned_start: wo.planned_start || "", planned_end: wo.planned_end || "",
  labor_rate: wo.labor_rate || 0,
  operations: wo.operations || [], materials: wo.materials || [],
  equipment_status: wo.equipment_status || "", intervention_system: wo.intervention_system || "",
  hourmeter_start: wo.hourmeter_start || 0, hourmeter_end: wo.hourmeter_end || 0,
  planned_tools: wo.planned_tools || [], unplanned_tools: wo.unplanned_tools || [],
  shift: wo.shift || "", technicians_count: wo.technicians_count || 1,
  supervisor: wo.supervisor || "",
  ...patch,
});

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-[10px] font-bold uppercase tracking-widest">{label}</Label>
    {children}
  </div>
);

export const WOEditDialog = ({ wo, open, onOpenChange, onSaved }) => {
  const { t } = useI18n();
  const { can } = useAuth();
  const [form, setForm] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [users, setUsers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [saving, setSaving] = useState(false);
  const isAdmin = can("admin");

  useEffect(() => {
    if (!open || !wo) { setForm(null); return; }
    setForm({ type: wo.type, priority: wo.priority, equipment_id: wo.equipment_id, description: wo.description,
      assigned_to: wo.assigned_to || "", technician_name: wo.technician_name || "", labor_rate: wo.labor_rate || 0,
      planned_start: (wo.planned_start || "").slice(0, 10), planned_end: (wo.planned_end || "").slice(0, 10) });
    api.get("/equipment", { params: { active: true } }).then((r) => setEquipment(r.data)).catch(() => {});
    api.get("/mnt/technicians", { params: { active: true } }).then((r) => setTechnicians(r.data)).catch(() => {});
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [open, wo, isAdmin]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/mnt/work-orders/${wo.id}`, woPutBody(wo, { ...form, labor_rate: Number(form.labor_rate) || 0 }));
      toast.success(t("mnt.wo.edited"));
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!form || !wo) return null;
  const set = (patch) => setForm({ ...form, ...patch });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:max-w-lg" data-testid="wo-edit-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase font-black">{t("mnt.wo.edit")} · {wo.wo_number}</DialogTitle>
          <DialogDescription className="text-xs">{t("mnt.wo.editDesc")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("mnt.wo.type")}>
            <Select value={form.type} onValueChange={(v) => set({ type: v })}>
              <SelectTrigger className="rounded-none h-8 text-xs" data-testid="wo-edit-type"><SelectValue /></SelectTrigger>
              <SelectContent>{WO_TYPES.map((x) => <SelectItem key={x} value={x}>{t(`mnt.wo.type.${x}`)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("mnt.priority")}>
            <Select value={form.priority} onValueChange={(v) => set({ priority: v })}>
              <SelectTrigger className="rounded-none h-8 text-xs" data-testid="wo-edit-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{t(`mnt.p.${p}`)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="col-span-2">
            <Field label={t("mnt.equipment")}>
              <Select value={form.equipment_id} onValueChange={(v) => set({ equipment_id: v })}>
                <SelectTrigger className="rounded-none h-8 text-xs" data-testid="wo-edit-equipment"><SelectValue /></SelectTrigger>
                <SelectContent>{equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label={t("mnt.wo.plannedStart")}>
            <Input type="date" value={form.planned_start} onChange={(e) => set({ planned_start: e.target.value })} className="rounded-none h-8 text-xs" data-testid="wo-edit-planned-start" />
          </Field>
          <Field label={t("mnt.wo.plannedEnd")}>
            <Input type="date" value={form.planned_end} onChange={(e) => set({ planned_end: e.target.value })} className="rounded-none h-8 text-xs" data-testid="wo-edit-planned-end" />
          </Field>
          <Field label={t("mnt.wo.assigned")}>
            {isAdmin ? (
              <Select value={form.assigned_to || "none"} onValueChange={(v) => set({ assigned_to: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-none h-8 text-xs" data-testid="wo-edit-assigned"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {users.filter((u) => ["maintenance", "warehouse", "admin"].includes(u.role)).map((u) =>
                    <SelectItem key={u.id} value={u.email}>{u.name} · {u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input value={form.assigned_to} onChange={(e) => set({ assigned_to: e.target.value })} className="rounded-none h-8 text-xs" data-testid="wo-edit-assigned" />
            )}
          </Field>
          <Field label={t("mnt.wo.laborRate")}>
            <Input type="number" min="0" step="0.01" value={form.labor_rate} onChange={(e) => set({ labor_rate: e.target.value })} className="rounded-none h-8 text-xs" data-testid="wo-edit-labor-rate" />
          </Field>
          <div className="col-span-2">
            <Field label={t("mnt.wo.technician")}>
              {technicians.length > 0 && (
                <Select value={technicians.some((x) => x.name === form.technician_name) ? form.technician_name : "none"} onValueChange={(v) => set({ technician_name: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-none h-8 text-xs" data-testid="wo-edit-technician-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {technicians.map((x) => <SelectItem key={x.id} value={x.name}>{x.name}{x.position ? ` · ${x.position}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Input value={form.technician_name} onChange={(e) => set({ technician_name: e.target.value })} className="rounded-none h-8 text-xs" placeholder="Nombre del mecánico" data-testid="wo-edit-technician" />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label={t("mnt.description")}>
              <Textarea rows={3} value={form.description} onChange={(e) => set({ description: e.target.value })} className="rounded-none text-xs" data-testid="wo-edit-description" />
            </Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none text-xs font-bold" data-testid="wo-edit-cancel">{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving || !form.equipment_id || !form.description.trim()} className="rounded-none text-xs font-bold" data-testid="wo-edit-save">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const WOCancelDialog = ({ wo, open, onOpenChange, onDone }) => {
  const { t } = useI18n();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/mnt/work-orders/${wo.id}/status`, { status: "cancelled", cancel_reason: reason.trim() });
      toast.success(t("mnt.wo.cancelled"));
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!wo) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:max-w-md" data-testid="wo-cancel-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase font-black text-red-700">{t("mnt.wo.cancel")} · {wo.wo_number}</DialogTitle>
          <DialogDescription className="text-xs">{t("mnt.wo.cancelDesc")}</DialogDescription>
        </DialogHeader>
        <div className="text-xs"><span className="font-bold">{wo.equipment_code}</span> · {wo.description}</div>
        <Field label={t("mnt.wo.cancelReason")}>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-none text-xs" placeholder={t("mnt.wo.cancelReasonPh")} data-testid="wo-cancel-reason" />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none text-xs font-bold" data-testid="wo-cancel-back">{t("common.cancel")}</Button>
          <Button variant="destructive" onClick={confirm} disabled={busy || reason.trim().length < 3} className="rounded-none text-xs font-bold" data-testid="wo-cancel-confirm">{t("mnt.wo.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const WOReopenDialog = ({ wo, open, onOpenChange, onDone }) => {
  const { t } = useI18n();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/mnt/work-orders/${wo.id}/reopen`, { reason: reason.trim() });
      toast.success(t("mnt.wo.reopened"));
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!wo) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:max-w-md" data-testid="wo-reopen-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase font-black">{t("mnt.wo.reopen")} · {wo.wo_number}</DialogTitle>
          <DialogDescription className="text-xs">{t("mnt.wo.reopenDesc")}</DialogDescription>
        </DialogHeader>
        <div className="text-xs"><span className="font-bold">{wo.equipment_code}</span> · {wo.description}</div>
        {wo.cancel_reason && <div className="text-[11px] text-muted-foreground border-l-2 pl-2">{t("mnt.wo.cancelReason")}: {wo.cancel_reason}</div>}
        <Field label={t("mnt.wo.reopenReason")}>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-none text-xs" placeholder={t("mnt.wo.reopenReasonPh")} data-testid="wo-reopen-reason" />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none text-xs font-bold" data-testid="wo-reopen-back">{t("common.cancel")}</Button>
          <Button onClick={confirm} disabled={busy || reason.trim().length < 3} className="rounded-none text-xs font-bold" data-testid="wo-reopen-confirm">{t("mnt.wo.reopen")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
